import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  BookOpen, Film, Music, Gamepad2, Mic, Globe, Users, 
  ExternalLink, Play, Filter, Palette, PenTool
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const AMAZON_AFFILIATE_TAG = "beckenham-21";

// Helper function to add affiliate tag to Amazon links
const addAmazonAffiliateTag = (url) => {
  if (!url) return url;
  
  // Check if it's an Amazon link
  const amazonDomains = ['amazon.co.uk', 'amazon.com', 'amazon.de', 'amazon.fr', 'amazon.es', 'amazon.it', 'amzn.to', 'amzn.eu'];
  const isAmazonLink = amazonDomains.some(domain => url.toLowerCase().includes(domain));
  
  if (!isAmazonLink) return url;
  
  try {
    const urlObj = new URL(url);
    // Remove existing tag if present and add our affiliate tag
    urlObj.searchParams.delete('tag');
    urlObj.searchParams.set('tag', AMAZON_AFFILIATE_TAG);
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, try simple string append
    if (url.includes('?')) {
      // Remove existing tag parameter if present
      const cleanUrl = url.replace(/[?&]tag=[^&]*/g, '').replace(/\?&/, '?').replace(/&&/, '&');
      return cleanUrl + (cleanUrl.includes('?') ? '&' : '?') + `tag=${AMAZON_AFFILIATE_TAG}`;
    }
    return url + `?tag=${AMAZON_AFFILIATE_TAG}`;
  }
};

const categoryIcons = {
  books: BookOpen,
  films: Film,
  videos: Play,
  games: Gamepad2,
  music: Music,
  celebrities: Users,
  podcasts: Mic,
  websites: Globe,
  social_media: Users,
  art: Palette,
  poems: PenTool
};

const categoryLabels = {
  books: "Books",
  films: "Films & DVDs",
  videos: "Videos",
  games: "Video Games",
  music: "Songs & Music",
  celebrities: "Famous Fans",
  podcasts: "Podcasts",
  websites: "Websites",
  social_media: "Social Media",
  art: "Art",
  poems: "Poems & Songs"
};

const EntertainmentCard = ({ item }) => {
  const Icon = categoryIcons[item.category] || BookOpen;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 group" data-testid={`entertainment-${item.id}`}>
      {item.image_url ? (
        <div className="h-40 overflow-hidden">
          <img 
            src={item.image_url} 
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : (
        <div className="h-40 bg-gradient-to-br from-[#7C2C3B] to-[#63232F] flex items-center justify-center">
          <Icon className="w-16 h-16 text-white/30" />
        </div>
      )}
      
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge-blue text-xs">{categoryLabels[item.category]}</span>
          {item.year && <span className="text-xs text-slate-400">{item.year}</span>}
        </div>
        
        <h3 className="text-lg font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
          {item.title}
        </h3>
        
        <p className="text-sm text-slate-600 line-clamp-3">{item.description || item.summary}</p>
        
        {item.link && (
          <a 
            href={addAmazonAffiliateTag(item.link)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-4 text-sm text-[#2BAEE5] hover:text-[#7C2C3B] transition-colors"
          >
            Visit <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
};

export default function EntertainmentPage() {
  const [entertainment, setEntertainment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = Object.keys(categoryLabels);

  useEffect(() => {
    fetchEntertainment();
  }, [selectedCategory]);

  const fetchEntertainment = async () => {
    try {
      const params = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API}/entertainment${params}`);
      setEntertainment(response.data);
    } catch (error) {
      console.error("Failed to fetch entertainment:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="entertainment-page">
      <SEO title="Entertainment" path="/entertainment" description="West Ham United entertainment and media. Books, DVDs, podcasts and fan content about the Hammers." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            ENTERTAINMENT
          </h1>
          <p className="text-white/80">West Ham in books, films, music, and more</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === "all"
                  ? "bg-[#7C2C3B] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            {categories.map(cat => {
              const Icon = categoryIcons[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                    selectedCategory === cat
                      ? "bg-[#7C2C3B] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${cat}`}
                >
                  <Icon className="w-4 h-4" />
                  {categoryLabels[cat]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : entertainment.length === 0 ? (
          <div className="empty-state">
            <Film className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              NO ENTERTAINMENT ITEMS YET
            </h3>
            <p className="text-slate-500">Admin can add entertainment items via the admin panel.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-slate-500">
              Showing {entertainment.length} item{entertainment.length !== 1 ? 's' : ''}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {entertainment.map(item => (
                <EntertainmentCard key={item.id} item={item} />
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
